import { TypedDocumentNode, gql } from '@apollo/client';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { NBALogo } from '@sorare/core/src/atoms/icons/NBALogo';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayM, LabelL } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  mediumMobileHeightAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useNBAEvents } from '@sorare/gameplay/src/lib/events';

import {
  NBAOnboardingBuildYourClubQuery,
  NBAOnboardingBuildYourClubQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  flex: 1;
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    gap: var(--quadruple-unit);
  }
`;

const StyledButton = styled(Button)`
  margin-top: auto;
`;

const CardsWrapper = styled.div`
  display: grid;
  margin: auto 0;
  min-height: 340px;
  transform: rotate(-3deg);
  justify-content: center;
  gap: var(--intermediate-unit);
  grid-template-columns: repeat(4, 100px);

  @media ${mediumMobileHeightAndAbove} {
    grid-template-columns: repeat(4, 120px);
  }
`;

const CardImg = styled(ResponsiveImg)`
  width: 100%;
  pointer-events: none;
  user-select: none;
`;

type Props = {
  setStep: (n: number) => void;
};

const query = gql`
  query NBAOnboardingBuildYourClubQuery {
    commonPlayers(sport: NBA, first: 8, quality: TIER_0) {
      nodes {
        id
        anyPlayer {
          slug
          displayName
        }
        pictureUrl
      }
    }
  }
` as TypedDocumentNode<
  NBAOnboardingBuildYourClubQuery,
  NBAOnboardingBuildYourClubQueryVariables
>;

export const BuildYourClub = ({ setStep }: Props) => {
  const track = useNBAEvents();
  const { data } = useQuery(query);

  useEffect(() => {
    track('View Onboarding Build your club');
  }, [track]);

  const players = data?.commonPlayers.nodes || [];

  return (
    <Wrapper>
      <Vertical gap={2} center>
        <Horizontal gap={1}>
          <StarBall width={40} height={40} />
          <FontAwesomeIcon
            icon={faXmark}
            size="xl"
            color="rgba(var(--c-rgb-neutral-1000), 0.4)"
          />
          <NBALogo height={40} width={24} />
        </Horizontal>
        <DisplayM className="text-center">
          <FormattedMessage
            id="S3Onboarding.BuildYourClub.title"
            defaultMessage="You’re ready to build your club!"
          />
        </DisplayM>
        <LabelL color="var(--c-neutral-800)" className="text-center">
          <FormattedMessage
            id="S3Onboarding.BuildYourClub.collection"
            defaultMessage="Time to start your collection"
          />
        </LabelL>
      </Vertical>
      <CardsWrapper>
        {players.map(player => (
          <CardImg
            key={player.id}
            cropWidth={320}
            src={player.pictureUrl}
            alt={player.anyPlayer.displayName}
          />
        ))}
      </CardsWrapper>
      <StyledButton
        color="secondary"
        onClick={() => setStep(3)}
        fullWidth
        size="large"
      >
        <FormattedMessage
          id="S3Onboarding.BuildYourClub.cta"
          defaultMessage="Let's go!"
        />
      </StyledButton>
    </Wrapper>
  );
};
